import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../authContext';
import { getSavedIdeas } from '../utils/searchService';
import SaveButton from '../components/SaveButton';
import { supabase } from './supabaseClient'; // Import supabase
import { getCached } from '../utils/simpleCache';
import './JourneyPage.css'; // Reusing Journey CSS for consistent grid
import NothingHereIcon from '../assets/nothing-here icon.png';

const SavedPage = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [savedIdeas, setSavedIdeas] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchSaved = async () => {
            if (!currentUser) {
                setLoading(false);
                return;
            }
            try {
                // 1. Fetch saved ideas
                const ideas = await getSavedIdeas(currentUser.uid);

                // 2. Fetch signed URLs for images with Caching & Optimization
                const ideasWithImages = await Promise.all(ideas.map(async (idea) => {
                    let imageUrl = null;
                    const cacheKey = `saved_idea_img_${idea.id}`;

                    // If it's a full URL (http/https), use it directly
                    if (idea.imagePath && (idea.imagePath.startsWith('http') || idea.imagePath.startsWith('blob'))) {
                        imageUrl = idea.imagePath;
                    }
                    // Otherwise, treat as Supabase storage path
                    else if (idea.imagePath) {
                        try {
                            imageUrl = await getCached(cacheKey, async () => {
                                const { data: signed, error } = await supabase.storage
                                    .from('images')
                                    .createSignedUrl(idea.imagePath, 60 * 60, {
                                        transform: {
                                            width: 400,
                                            height: 300,
                                            resize: 'cover',
                                        },
                                    });

                                if (error) throw error;
                                return signed ? signed.signedUrl : null;
                            }, 50 * 60 * 1000); // Cache for 50 mins
                        } catch (err) {
                            console.error("Error fetching image for saved idea:", err);
                        }
                    }
                    return { ...idea, imageUrl }; // Add imageUrl to idea object
                }));

                setSavedIdeas(ideasWithImages);
            } catch (error) {
                console.error("Error fetching saved ideas:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchSaved();
    }, [currentUser]);

    const handleUnsave = (id) => {
        // Immediately remove the item from the list
        setSavedIdeas(prev => prev.filter(item => item.id !== id));
    };

    if (!currentUser) {
        // Redirect or show login prompt if needed, but for now just show empty/loading
        return <div className="journey-page"><div className="journey-loading">Please login to view saved ideas.</div></div>;
    }

    if (loading) {
        return (
            <div className="journey-page">
                <div className="journey-loading">
                    <div className="spinner"></div> {/* Use a simpler loader or just text */}
                    Loading saved ideas...
                </div>
            </div>
        );
    }

    return (
        <div className="journey-page" style={{ paddingTop: '80px' }}>
            {/* Fixed Header */}
            <div className="journey-header" style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                height: '60px',

                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',

            }}>
                <img
                    src={require('../assets/back.png')}
                    alt="Back"
                    className="journey-back-btn"
                    style={{ position: 'absolute', left: '20px', cursor: 'pointer', width: '20px', height: '20px' }}
                    onClick={() => navigate(-1)}
                />
                <h1 className="journey-title" style={{ margin: 0, fontSize: '1.2rem', textAlign: 'center' }}>Saved Ideas</h1>
            </div>

            <div className="journey-ideas-section" style={{ marginTop: '20px' }}>
                {savedIdeas.length > 0 ? (
                    <div className="journey-ideas-grid">
                        {savedIdeas.map((idea) => (
                            <div
                                key={idea.id}
                                className="journey-idea-card"
                                onClick={() => navigate(`/view-idea/${idea.id}`, { state: { idea } })}
                            >
                                {idea.imageUrl && (
                                    <div className="journey-idea-image-container" style={{ position: 'relative' }}>
                                        <img
                                            src={idea.imageUrl} // Use generated signed URL
                                            // searchService returns: imagePath: ideaData.imagePath || ideaData.image || null,
                                            alt={idea.title}
                                            className="journey-idea-image"
                                            loading="lazy"
                                        />
                                        {/* Save Button Overlay */}
                                        <div style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 20 }}>
                                            <SaveButton
                                                userId={currentUser.uid}
                                                ideaId={idea.id}
                                                onToggle={(isSaved) => {
                                                    if (!isSaved) handleUnsave(idea.id);
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                                <div className="journey-idea-content">
                                    <h4 className="journey-idea-title">{idea.title}</h4>
                                    {idea.tags && idea.tags.length > 0 && (
                                        <div className="journey-idea-tags">
                                            {idea.tags.slice(0, 3).map((tag, idx) => (
                                                <span key={idx} className="journey-idea-tag">
                                                    #{tag}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="journey-empty-state">
                        <img
                            src={NothingHereIcon}
                            alt="Nothing here"
                            className="journey-empty-icon"
                            style={{ width: '120px', height: '120px', objectFit: 'contain', opacity: 0.8 }}
                        />
                        <h3 className="journey-empty-text">Nothing added here...</h3>
                        <p className="journey-empty-subtext">Save inspiring ideas to build your collection.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SavedPage;
